import moment from 'moment';
import { Control, Controller, FieldErrors } from 'react-hook-form';
import styled from 'styled-components';

import { Dropdown, DropdownContainer, DropdownItem } from '../../Dropdown.tsx';
import { FormLabel } from '../../FormLabel';
import { DateInput, Input } from '../../Input';
import { TremfyaExpressEnrollmentFormDataDefaultValues } from '../../../api';
import { formatPhoneNumber, requiredErrorMessage, requiredErrorString, titleCase } from '../../../utils.tsx';
import { CardSection } from './sharedStyles.tsx';
import { sexValues, phoneTypeValues, stateValues } from './dropdownValues.ts';
import { ControllerWrapper } from '../../legacySignup/ControllerWrapper.tsx';
import { TherapyTask } from '../../../api';
import { EMAIL_VALIDATION_REGEX, PHONE_VALIDATION_REGEX } from '../../../constants.ts';
import { Typography } from 'design-system/src/components/Typography/Typography.tsx';

const StyledTremfyaEnrollmentPatientSection = styled(CardSection)`
  gap: 16px;
  container-name: form-container;
  container-type: inline-size;
`;

const Row = styled.div`
  display: grid;
  gap: 16px;

  @container form-container (width < 528px) {
    grid-template-columns: 1fr !important;
  }
`;

const NameRow = styled(Row)`
  grid-template-columns: 1.5fr 1.5fr 1fr;
`;

const EmailRow = styled(Row)`
  grid-template-columns: 3fr 1fr;
`;

const PhoneRow = styled(Row)`
  grid-template-columns: 1fr 1fr;
`;

const CityRow = styled(Row)`
  grid-template-columns: 2fr 1fr 1fr;
`;

const getSexOptions = () => {
  return sexValues.map((sex) => (
    <DropdownItem key={sex} value={sex}>
      {sex}
    </DropdownItem>
  ));
};

const getPhoneTypeOptions = () => {
  return phoneTypeValues.map((phoneType) => (
    <DropdownItem key={phoneType} value={phoneType}>
      {titleCase(phoneType)}
    </DropdownItem>
  ));
};

const getStateOptions = () => {
  return stateValues.map((state) => (
    <DropdownItem key={state} value={state}>
      {state}
    </DropdownItem>
  ));
};

export type TremfyaEnrollmentPatientSectionProps = {
  control: Control<TremfyaExpressEnrollmentFormDataDefaultValues>;
  errors: FieldErrors<TremfyaExpressEnrollmentFormDataDefaultValues>;
  therapyTaskValue: TherapyTask;
};

/**
 * The patient section of the Tremfya Express Enrollment task.
 */
export const TremfyaEnrollmentPatientSection = ({
  control,
  errors,
  therapyTaskValue,
}: TremfyaEnrollmentPatientSectionProps) => {
  return (
    <StyledTremfyaEnrollmentPatientSection>
      <Typography styledAs="bodyDMSans" renderedAs="h2" weight={600}>
        Patient information
      </Typography>
      <NameRow>
        <ControllerWrapper>
          <Controller
            control={control}
            name="patient_first_name"
            rules={{ required: true }}
            render={({ field }) => (
              <Input
                label="First name"
                {...field}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  field.onChange(event.target.value);
                }}
                hasError={!!errors.patient_first_name}
              />
            )}
          />
          {errors.patient_first_name && requiredErrorMessage}
        </ControllerWrapper>
        <ControllerWrapper>
          <Controller
            control={control}
            name="patient_last_name"
            rules={{ required: true }}
            render={({ field }) => (
              <Input
                label="Last name"
                {...field}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  field.onChange(event.target.value);
                }}
                hasError={!!errors.patient_last_name}
              />
            )}
          />
          {errors.patient_last_name && requiredErrorMessage}
        </ControllerWrapper>
        <ControllerWrapper>
          <Controller
            name="patient_sex"
            control={control}
            rules={{ required: true }}
            render={({ field }) => (
              <DropdownContainer>
                <FormLabel id="sex-label">Sex</FormLabel>
                <Dropdown labelId="sex-label" {...field} $hasError={!!errors.patient_sex}>
                  {getSexOptions()}
                </Dropdown>
                {errors.patient_sex && requiredErrorMessage}
              </DropdownContainer>
            )}
          />
        </ControllerWrapper>
      </NameRow>
      <EmailRow>
        <ControllerWrapper>
          <Controller
            control={control}
            name="patient_email"
            rules={{
              required: {
                value: therapyTaskValue === TherapyTask.NurseGuide ? true : false,
                message: requiredErrorString,
              },
              pattern: {
                value: EMAIL_VALIDATION_REGEX,
                message: 'Invalid email',
              },
            }}
            render={({ field }) => {
              const label =
                therapyTaskValue === TherapyTask.NurseGuide
                  ? 'Email (must be real and unique to the patient)'
                  : 'Email (Optional)';
              return (
                <Input
                  label={label}
                  {...field}
                  value={field.value || ''}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    field.onChange(event.target.value);
                  }}
                  hasError={!!errors.patient_email}
                />
              );
            }}
          />
          {errors.patient_email && errors.patient_email.message}
        </ControllerWrapper>
        <ControllerWrapper>
          <Controller
            control={control}
            name="patient_dob"
            rules={{ required: true }}
            render={({ field }) => (
              <DateInput
                label="Date of birth"
                value={field.value ? moment(field.value) : null}
                onChange={(value) => {
                  field.onChange(value ? value.toISOString() : '');
                }}
                hasError={!!errors.patient_dob}
              />
            )}
          />
          {errors.patient_dob && requiredErrorMessage}
        </ControllerWrapper>
      </EmailRow>
      <PhoneRow>
        <ControllerWrapper>
          <Controller
            control={control}
            name="patient_phone"
            rules={{
              required: {
                value: true,
                message: requiredErrorString,
              },
              pattern: {
                value: PHONE_VALIDATION_REGEX,
                message: 'Invalid phone',
              },
            }}
            render={({ field }) => (
              <Input
                label="Phone"
                {...field}
                value={field.value ? formatPhoneNumber(field.value) : ''}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  field.onChange(event.target.value);
                }}
                hasError={!!errors.patient_phone}
              />
            )}
          />
          {errors.patient_phone && errors.patient_phone.message}
        </ControllerWrapper>
        <ControllerWrapper>
          <Controller
            name="patient_phone_type"
            control={control}
            rules={{ required: true }}
            render={({ field }) => (
              <DropdownContainer>
                <FormLabel id="phone-type-label">Type</FormLabel>
                <Dropdown labelId="phone-type-label" {...field} $hasError={!!errors.patient_phone_type}>
                  {getPhoneTypeOptions()}
                </Dropdown>
              </DropdownContainer>
            )}
          />
          {errors.patient_phone_type && requiredErrorMessage}
        </ControllerWrapper>
      </PhoneRow>
      <ControllerWrapper>
        <Controller
          control={control}
          name="patient_address"
          rules={{ required: true }}
          render={({ field }) => (
            <Input
              label="Street address"
              {...field}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                field.onChange(event.target.value);
              }}
              hasError={!!errors.patient_address}
            />
          )}
        />
        {errors.patient_address && requiredErrorMessage}
      </ControllerWrapper>
      <CityRow>
        <ControllerWrapper>
          <Controller
            control={control}
            name="patient_city"
            rules={{ required: true }}
            render={({ field }) => (
              <Input
                label="City"
                {...field}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  field.onChange(event.target.value);
                }}
                hasError={!!errors.patient_city}
              />
            )}
          />
          {errors.patient_city && requiredErrorMessage}
        </ControllerWrapper>
        <ControllerWrapper>
          <Controller
            name="patient_state"
            control={control}
            rules={{ required: true }}
            render={({ field }) => (
              <DropdownContainer>
                <FormLabel id="state-label">State</FormLabel>
                <Dropdown labelId="state-label" {...field} $hasError={!!errors.patient_state}>
                  {getStateOptions()}
                </Dropdown>
              </DropdownContainer>
            )}
          />
          {errors.patient_state && requiredErrorMessage}
        </ControllerWrapper>
        <ControllerWrapper>
          <Controller
            control={control}
            name="patient_zip"
            rules={{ required: true }}
            render={({ field }) => (
              <Input
                label="Zip code"
                {...field}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  field.onChange(event.target.value);
                }}
                hasError={!!errors.patient_zip}
              />
            )}
          />
          {errors.patient_zip && requiredErrorMessage}
        </ControllerWrapper>
      </CityRow>
    </StyledTremfyaEnrollmentPatientSection>
  );
};

export default TremfyaEnrollmentPatientSection;
